import { useCallback, useMemo } from 'react';
import { matchPath, Route as BaseRoute, RouteComponentProps, RouteProps } from 'react-router-dom';
import { useSession } from '@distribusion/portal-lib/auth';
import { ErrorPage } from '@distribusion/portal-lib/components';
import * as Sentry from '@sentry/react';
import { usePermissions } from 'api/permissions';
import { ROUTES } from 'constants/common';
import { featureFlags } from 'lib/feature-flags';

const Route = Sentry.withSentryRouting(BaseRoute);

export const PrivateRoute = ({ component: Component, ...rest }: RouteProps) => {
  const { data: sessionData } = useSession();
  const { data: permissions } = usePermissions();

  const accessMap = useMemo(
    () => ({
      [ROUTES.USERS.INDEX]: permissions?.view_users,
      [ROUTES.USERS.CREATE]: permissions?.view_users,
      [ROUTES.USERS.DETAIL]: permissions?.view_users,
      [ROUTES.USERS.EDIT]: permissions?.view_users,
      [ROUTES.CONFIGURATION.SETTINGS]: permissions?.view_partner_settings,
      [ROUTES.AGENCY_MANAGEMENT.PAYMENT_INFORMATION]: permissions?.view_payment_information,
      [ROUTES.AGENCY_MANAGEMENT.GUIDELINES]: permissions?.view_guidelines,
      [ROUTES.AGENCY_MANAGEMENT.PAYPAL_COMMISSION]: permissions?.view_paypal_commission,
      [ROUTES.BOOKING_MANAGEMENT.INDEX]: permissions?.view_bookings,
      [ROUTES.BOOKING_MANAGEMENT.NEW]: permissions?.view_book_a_trip,
      [ROUTES.BOOKING_MANAGEMENT.PAY]: permissions?.view_bookings,
      [ROUTES.BOOKING_MANAGEMENT.HELD_BOOKINGS]: permissions?.view_reservations_page,
      [ROUTES.BOOKING_MANAGEMENT.RETAILER_CANCELLATIONS]:
        permissions?.view_bookings_cancellations_tab,
      [ROUTES.BOOKING_MANAGEMENT.BOOKING_DETAIL]: permissions?.view_bookings,
      [ROUTES.BOOKING_MANAGEMENT.HELD_BOOKING_DETAIL]: permissions?.view_reservations_page,
      [ROUTES.BOOKING_MANAGEMENT.AMEND]: permissions?.view_bookings,
      [ROUTES.BOOKING_MANAGEMENT.TICKET_VALIDATOR]: permissions?.view_ticket_validator,
      [ROUTES.BOOKING_MANAGEMENT.CANCELLATIONS]: permissions?.view_cancellations_page,
      [ROUTES.BOOKING_MANAGEMENT.SUBSCRIPTIONS]: permissions?.manage_subscriptions,
      [ROUTES.BOOKING_MANAGEMENT.SUBSCRIPTION_DETAIL]: permissions?.manage_subscriptions,
      [ROUTES.BOOKING_MANAGEMENT.CUSTOMERS]: permissions?.manage_user_accounts,
      [ROUTES.DASHBOARDS.PARTNER]:
        permissions?.view_sales_dashboard ||
        permissions?.view_departure_deep_dive ||
        permissions?.view_ancillary_insights,
      [ROUTES.DASHBOARDS.GLOBAL_INVENTORY]: permissions?.view_global_inventory,
      [ROUTES.DASHBOARDS.SLA]: permissions?.view_sla,
      [ROUTES.DASHBOARDS.PASSENGER_MANIFEST]: permissions?.view_passenger_manifest,
      [ROUTES.FINANCES.FINANCIAL_DOCUMENTS]:
        featureFlags.financialDocuments && permissions?.view_finance,
      [ROUTES.TRADING_DESK.INDEX]: permissions?.view_trading_desk,
      [ROUTES.TRADING_DESK.CARRIER_PROFILE]: permissions?.view_trading_desk,
      [ROUTES.TRADING_DESK.RETAILERS]: permissions?.view_retailer_tab,
      [ROUTES.TRADING_DESK.RETAILER_PROFILE]: permissions?.view_retailer_tab,
      [ROUTES.PRICING.INDEX]: permissions?.view_pricing,
      [ROUTES.CONFIGURATION.WEBSITE_CONFIGURATION]:
        permissions?.view_whitelabel_customization_page || permissions?.edit_whitelabel_settings,
      [ROUTES.CONFIGURATION.SEARCH_PAGE_EDITOR]: permissions?.edit_whitelabel_settings,
      [ROUTES.CONFIGURATION.LANDING_PAGE_EDITOR]: permissions?.edit_whitelabel_settings,
      [ROUTES.CONFIGURATION.UPCOMING_FEATURES_EDITOR]: permissions?.view_prototypes,
      [ROUTES.CONFIGURATION.UPCOMING_FEATURES_PREVIEW]: permissions?.view_prototypes,
    }),
    [permissions],
  );

  const getPath = useCallback(
    (pathname: string) =>
      Object.keys(accessMap).includes(pathname)
        ? pathname
        : Object.keys(accessMap)
            .filter((route) => matchPath(pathname, { path: route }))
            .reduce((a: string, b: string) => (a.length > b.length ? a : b), ''),
    [accessMap],
  );

  const isAllowed = useCallback(
    (props: RouteComponentProps) => {
      const path =
        props.location.pathname === props.match.url
          ? props.match.path
          : getPath(props.location.pathname);

      if (!Object.keys(accessMap).includes(path)) {
        return true;
      }

      if (
        [ROUTES.USERS.DETAIL, ROUTES.USERS.EDIT].includes(path) &&
        (sessionData?.identity.id === (props.match.params as { id: string }).id ||
          props.location.pathname.includes(sessionData?.identity.id))
      ) {
        return true;
      }

      return accessMap[path];
    },
    [accessMap, sessionData?.identity.id, getPath],
  );

  const renderFunction: RouteProps['render'] = useCallback(
    (props) => (isAllowed(props) ? <Component {...props} /> : <ErrorPage code={403} />),
    [isAllowed, Component],
  );

  return <Route {...rest} render={renderFunction} />;
};

export default PrivateRoute;
